import { useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import type { SwitchList } from '../types/interchange'

export interface SwitchListSubscriptionResult {
  /** Transient UI notice (client state) — cleared after 5s. */
  recomputeNotice: string | null
}

/**
 * Subscribes to `SWITCH_LIST_UPDATED` events for a work order.
 *
 * ADR-002: the hook never keeps a parallel copy of server state — it
 * invalidates React Query keys so query-backed consumers refetch, and
 * surfaces the payload via `onRecomputed` for prop-driven views
 * (SwitchListDrawer has no GET-by-id endpoint to refetch against).
 * Only the transient notice is held as client UI state.
 */
export function useSwitchListSubscription(
  workOrderId: string | undefined,
  subscribe: (callback: (message: any) => void) => () => void,
  onRecomputed?: (switchList: SwitchList) => void
): SwitchListSubscriptionResult {
  const queryClient = useQueryClient()
  const [recomputeNotice, setRecomputeNotice] = useState<string | null>(null)
  const onRecomputedRef = useRef(onRecomputed)
  onRecomputedRef.current = onRecomputed

  useEffect(() => {
    if (!workOrderId) {
      return
    }
    return subscribe((message: any) => {
      if (message?.type === 'SWITCH_LIST_UPDATED' && message.payload?.id === workOrderId) {
        queryClient.invalidateQueries({ queryKey: ['switchlist'] })
        queryClient.invalidateQueries({ queryKey: ['workorders'] })
        onRecomputedRef.current?.(message.payload as SwitchList)
        setRecomputeNotice('Switch list automatically recomputed due to track clearance change.')
      }
    })
  }, [workOrderId, subscribe, queryClient])

  useEffect(() => {
    if (!recomputeNotice) {
      return
    }
    const timer = setTimeout(() => setRecomputeNotice(null), 5000)
    return () => clearTimeout(timer)
  }, [recomputeNotice])

  return { recomputeNotice }
}
